import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import VideoPlayer from '@rover/components/src/video/VideoPlayer';
import KEYS from '@rover/constants/src/platform/keys.constants';
import { Box, Button, Flex, Text } from '@rover/kibble/core';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';
import { isReactNative } from '@rover/rsdk/src/modules/Network/userAgent';

import {
  DIMMED_IMAGE_OPACITY,
  MAX_LIGHTBOX_IMAGE_HEIGHT,
  MAX_LIGHTBOX_IMAGE_WIDTH,
} from './constants';
import { isDimmedImage, isGalleryVideoItem } from './ImageGallery.common';
import ImageWithShimmer from './ImageWithShimmer';
import type { LightboxPagerProps } from './LightboxPager.common';
import LightboxPagerControls from './LightboxPagerControls';
import { shouldLoadMore } from './utils';

const LightboxPager = ({
  images,
  lightboxIndex,
  setLightboxIndex,
  pagination,
  gallery,
  dimmedImageIds,
  autoPlayVideos,
  onVideoPlay,
  onVideoTimeUpdate,
  onVideoSeek,
}: LightboxPagerProps): JSX.Element => {
  const { t } = useLingui();
  const {
    currentUri,
    currentSize,
    bounds,
    placeholderSize,
    isCurrentImageFirst,
    isCurrentImageLast,
    hasNextImage,
    hasPrevImage,
    currentIndex,
  } = gallery;

  const [cachedImages, setCachedImages] = useState<{ [k: string]: boolean }>({});
  const pageRef = useRef<HTMLDivElement>(null);

  // The retry button unmounts once the request starts. The modal doesn't scope focus,
  // so focus falling to the body would leave Escape and Tab outside the lightbox.
  const retryLoad = useCallback((): void => {
    pageRef.current?.focus();
    pagination?.onLoadMore();
  }, [pagination]);

  const showPrev = useCallback((): void => {
    if (isCurrentImageFirst) return;
    setLightboxIndex((prevIndex) => prevIndex - 1);
  }, [isCurrentImageFirst, setLightboxIndex]);

  const showNext = useCallback((): void => {
    if (isCurrentImageLast) return;
    if (shouldLoadMore(pagination) && lightboxIndex >= images.length - 2) {
      pagination?.onLoadMore();
    }
    setLightboxIndex((prevIndex) => prevIndex + 1);
  }, [isCurrentImageLast, setLightboxIndex, pagination, lightboxIndex, images]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      // This listener is on `document`, so it hears arrows pressed anywhere —
      // including inside the video player, where they belong to the transport
      // controls and should scrub rather than change gallery item.
      const target = event.target as HTMLElement | null;
      if (target?.closest?.('video')) return;

      if (event.key === KEYS.LEFT) {
        showPrev();
      } else if (event.key === KEYS.RIGHT) {
        showNext();
      }
    },
    [showPrev, showNext]
  );

  useEffect(() => {
    if (process.env.JS_ENV_CLIENT) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
    return () => {};
  }, [handleKeyDown]);

  const isWaitingForImages = lightboxIndex >= images.length;
  const currentImage = images[currentIndex];
  const currentVideo = currentImage && isGalleryVideoItem(currentImage) ? currentImage : undefined;

  const renderCurrentItem = (): JSX.Element => {
    // Only the full view plays; the "view all" grid shows the thumbnail with a
    // play badge. Navigating away unmounts the player, whose own cleanup stops
    // playback.
    if (currentVideo) {
      return (
        // Sized like a photo: a percentage width would collapse to the video's
        // intrinsic width inside this shrink-to-fit page.
        <Box
          width={currentSize.width || MAX_LIGHTBOX_IMAGE_WIDTH}
          maxWidth="100%"
          height={isReactNative() ? MAX_LIGHTBOX_IMAGE_HEIGHT : undefined}
          testID="lightbox-video"
        >
          {/* `posterUrl` is web-only: expo-video shows the video's own first
              frame instead, so the tile-to-player transition is continuous on
              web and a jump to a possibly unrelated frame on native. Accepted
              rather than wired, so don't go looking for the native half. */}
          <VideoPlayer
            sources={currentVideo.video.sources}
            description={currentVideo.video.description}
            posterUrl={currentVideo.largeUncropped || undefined}
            autoPlay={autoPlayVideos}
            onPlay={onVideoPlay ? () => onVideoPlay(currentVideo) : undefined}
            onTimeUpdate={onVideoTimeUpdate}
            onSeek={onVideoSeek}
            maxHeight={isReactNative() ? undefined : `${bounds.height}px`}
          />
        </Box>
      );
    }

    // The page this photo is on failed, and the error banner is behind the modal.
    if (isWaitingForImages && pagination?.loadMoreFailed) {
      return (
        <Flex
          webProps={{ role: 'alert' }}
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          width={placeholderSize.width}
          height={isReactNative() ? MAX_LIGHTBOX_IMAGE_HEIGHT : placeholderSize.height}
          maxWidth="100%"
          data-testid="lightbox-load-error"
        >
          <Text textColor="primary_inverse" marginBottom="4x">
            {t`This photo could not be loaded.`}
          </Text>
          <Button variant="secondary" onPress={retryLoad} testID="lightbox-retry-button" contrast>
            {t`Try again`}
          </Button>
        </Flex>
      );
    }

    if (isWaitingForImages) {
      return (
        <LoadingShimmer
          width={placeholderSize.width}
          height={isReactNative() ? MAX_LIGHTBOX_IMAGE_HEIGHT : placeholderSize.height}
          borderRadius="primary"
          m="0x"
          data-testid="lightbox-loading-shimmer"
        />
      );
    }

    return (
      <ImageWithShimmer
        src={currentUri}
        caption={currentImage?.caption}
        altText={currentImage?.altText}
        width={currentSize.width || placeholderSize.width}
        height={
          isReactNative() ? MAX_LIGHTBOX_IMAGE_HEIGHT : currentSize.height || placeholderSize.height
        }
        cached={cachedImages[currentUri]}
        setCached={(cached: boolean) => {
          setCachedImages((prev) => ({ ...prev, [currentUri]: cached }));
        }}
      />
    );
  };

  const isCurrentImageDimmed = isDimmedImage(currentImage, dimmedImageIds);
  const isLoadingCurrentImage = isWaitingForImages && !currentVideo && !pagination?.loadMoreFailed;

  return (
    <Box position="relative" width="100%" textAlign="center">
      {/* Screen readers announce a change to a live region already on the page, not one mounted
          with its text, so the region stays mounted and only its text changes. */}
      <A11yHiddenBox webProps={{ role: 'status' }} data-testid="lightbox-loading-status">
        {isLoadingCurrentImage ? t`Loading photo` : ''}
      </A11yHiddenBox>
      <LightboxPagerControls
        hasPrev={hasPrevImage}
        hasNext={hasNextImage}
        onPrev={showPrev}
        onNext={showNext}
      />
      {/* A stage as tall as the bounds keeps the counter above and the caption below
          from moving as photos of different shapes page through. */}
      <Flex
        width="100%"
        height={isReactNative() ? undefined : bounds.height}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          ref={pageRef}
          webProps={{ tabIndex: -1 }}
          testID="lightbox-page"
          sx={isCurrentImageDimmed ? { opacity: DIMMED_IMAGE_OPACITY } : undefined}
        >
          {renderCurrentItem()}
        </Box>
      </Flex>
    </Box>
  );
};

export default LightboxPager;
